import Backbone from "backbone";

import _ from "underscore";

import View from "backdraft-app/src/view";
import ListView from "backdraft-app/src/listing/list";

import template from "./templates/list.jst";

import SortableListItem from "./item";

function newOptions(source) {
  if (source) {
    return Object.create(source);
  }

  return {};
}

function applyDefaultOptions(options = {}) {
  const result = newOptions(options);

  if (!options.fixedCollection) {
    result.fixedCollection = new Backbone.Collection();
  }

  return result;
}

export class SortableListItemsList extends ListView {
  get itemClass() { return SortableListItem; }

  constructor(options) {
    super({
      attributes: {
        "data-action": "sortable-list"
      },
      tagName: options.ordered ? "ol" : "ul",
      ...options
    });
  }
}

export class FixedListItemsList extends ListView {
  get itemClass() { return SortableListItem; }

  constructor(options) {
    super({
      attributes: {
        "data-action": "fixed-list",
        "start": options.startAt
      },
      tagName: options.ordered ? "ol" : "ul",
      ...options
    });
  }

  updateStartAt(value) {
    this.$el.attr("start", value);
  }
}

export default class SortableListView extends View {
  constructor(options) {
    super(options);

    this.options = applyDefaultOptions(options);

    this._verifyParameters();

    if (this.collection.size() === 0) {
      // TODO: handle zero size collection?
    }

    this.on("afterSwap", this.initializeSorting.bind(this));
    this.listenTo(this, "sortable-list:radio-item-selected", model => {
      this.setRadioSelection(model);
      this.render();
    });
  }

  initializeSorting() {
    if (!this.options.readOnly) {
      this.sortableInitialized = true;
      this.$("[data-action=sortable-list]").sortable({
        forcePlaceholderSize: true,
        handle: '.sortable-list__item',
        placeholder: 'sortable-list__item-placeholder'
      });
    }
  }

  close() {
    if (this.sortableInitialized) {
      this.$("[data-action=sortable-list]").sortable("destroy");
    }

    super.close();
  }

  render() {
    this.$el.html(template({ readOnly: this.options.readOnly, selection: this.options.selection, ordered: this.options.ordered }));

    const listOptions = _.extend({}, this.options.listOptions, {
      readOnly:   this.options.readOnly,
      selection:  this.options.selection,
      ordered:    this.options.ordered,
      collection: this.collection
    });

    const itemList = new SortableListItemsList(listOptions);
    this.child('item-list') && this.child('item-list').close();
    this.child('item-list', itemList);
    this.$("[data-mount='items']").html(itemList.render().$el);

    const fixedItemList = new FixedListItemsList({ collection: this.options.fixedCollection, readOnly: true, ordered: this.options.ordered, startAt: this.collection.size() + 1 });
    this.child('fixed-item-list') && this.child('fixed-item-list').close();
    this.child('fixed-item-list', fixedItemList);
    this.$("[data-mount='fixed-items']").html(fixedItemList.render().$el);

    this.listenTo(this.collection, "reset add remove", () => {
      fixedItemList.updateStartAt(this.collection.size() + 1);
    });

    return this;
  }

  addFixedItem(model) {
    this.options.fixedCollection.add(model);
  }

  removeFixedItem(model) {
    this.options.fixedCollection.remove(model);
  }

  resetSortableCollection(collection) {
    this.collection = collection;
  }

  resetFixedCollection(collection) {
    this.options.fixedCollection = collection;
  }

  sortedModels() {
    return this._sortedSortableListModels().concat(this.options.fixedCollection.models);
  }

  _sortedSortableListModels() {
    if (this.sortableInitialized) {
      const sortedItemIds = this.$("[data-action=sortable-list]").sortable("toArray", { attribute: "data-model-id" });
      return sortedItemIds.map(itemId => this.collection.findWhere({ id: itemId }));
    } else {
      return this.collection.models;
    }
  }

  getRadioSelection() {
    return this.collection.findWhere({ checked: true });
  }

  setRadioSelection(selectedModel) {
    const checkedModel = this.collection.findWhere({ id: selectedModel.id });
    if (typeof checkedModel !== "undefined" && !checkedModel.get("disabled")) {
      this.collection.models.forEach(model => {
        model.set("checked", false);
      });
      checkedModel.set("checked", true);
    }
  }

  _verifyParameters() {
    const missingParameters = [];
    let optionErrors = [];

    if (!this.collection) {
      missingParameters.push("collection");
    } else if (this.options.selection === "radio") {
      optionErrors = optionErrors.concat(this._verifyRadioSelectionParameters());
    }

    if (missingParameters.length !== 0) {
      throw new Error(`Missing required parameters: ${missingParameters.join(", ")}`);
    }

    if (optionErrors.length > 0) {
      throw new Error(`Option error(s): ${optionErrors.join(", ")}`);
    }
  }

  _verifyRadioSelectionParameters() {
    const errors = [];

    if (!this.options.readOnly) {
      errors.push('readOnly must be true for "radio" selection');
    }

    if (this.options.ordered) {
      errors.push('"ordered" is not compatible with "radio" selection');
    }

    if (this.collection.where({ checked: true }).length > 1) {
      errors.push('collection can not have more than 1 checked model for "radio" selection');
    }

    return errors;
  }
}
